import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

type Feature = {
    id: string;
    icon: string;
    img: string;
    highlighted?: boolean;
}

const FEATURES: Feature[] = [
    {
        id: 'revenue',
        icon: '/assets/analytics-up.svg',
        img: '/assets/revenue-sales-insights.svg',
        highlighted: true,
    },
    {
        id: 'costs',
        icon: '/assets/coins-dollar.svg',
        img: '/assets/cost-overview-suppliers-2.jpg',
    },
    {
        id: 'alerts',
        icon: '/assets/notification.svg',
        img: '/assets/price-alerts-cost-monitoring.svg',
    },
    {
        id: 'calculations',
        icon: '/assets/calculator.svg',
        img: '/assets/cost-price-overview.jpg',
    },
    // {
    //     id: 'reports',
    //     icon: '/assets/file.svg',
    //     img: '/assets/automated-reports-margin-insights.svg',
    // },
]

const ImageWithTransition: React.FC<{ src: string; alt: string; width?: number; height?: number }> = ({ src, alt, width, height }) => {
    const [entered, setEntered] = useState<boolean>(false)

    useEffect(() => {
        const t = setTimeout(() => setEntered(true), 20)
        return () => {
            clearTimeout(t)
            setEntered(false)
        }
    }, [src])

    return (
        <img
            src={src}
            alt={alt}
            width={width}
            height={height}
            className={`max-w-full h-auto rounded-[20px] mx-auto transition-all duration-500 ease-out transform ${entered ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
        />
    )
}

const OptimizeOperations: React.FC = () => {
    const { t } = useTranslation()
    const [activeId, setActiveId] = useState<string>(FEATURES[0].id)

    const active = FEATURES.find((f) => f.id === activeId) ?? FEATURES[0]

    return (
        <div id="features" className='bg-offwhite py-[30px] md:py-20 w-full px-5'>
            <div className='max-w-[1200px] mx-auto w-full'>
                <div className='flex items-center justify-between gap-4 flex-wrap'>
                    <h2 className='max-w-[552px] text-primary text-[30px] md:text-[48px] leading-10 md:leading-[58px] font-bold'>{t('landing.features.title')}</h2>
                    <p className='max-w-[379px] text-darkgray text-[16px] leading-[26px] font-semibold tracking-[0.01em]'>{t('landing.features.subtitle')}</p>
                </div>

                <div className='mt-6 md:mt-12 flex w-full justify-between items-stretch lg:flex-row flex-col gap-6'>
                    <div className='w-full lg:max-w-[529px] space-y-4'>
                        {FEATURES.map((feature, idx) => {
                            const isActive = feature.id === activeId
                            const title = t(`landing.features.${feature.id}.title`)
                            const subtitle = t(`landing.features.${feature.id}.subtitle`)
                            const baseClasses = isActive
                                ? 'shadow-[0px_20px_40px_0px_#148c691a] bg-accent text-white'
                                : 'bg-white text-primary'

                            return (
                                <button
                                    key={feature.id}
                                    onClick={() => setActiveId(feature.id)}
                                    aria-pressed={isActive}
                                    className={`w-full rounded-3xl p-3 md:p-5 flex items-center justify-between transition-all duration-200 ease-out transform-gpu ${baseClasses}`}
                                >
                                    <div className='flex items-center gap-4'>
                                        <div className={`w-12 h-12 min-w-12 rounded-[13px] flex items-center justify-center ${isActive ? 'bg-white' : 'bg-white border border-[#EEEEEE]'}`}>
                                            <img src={feature.icon} alt={title} width={24} height={24} />
                                        </div>
                                        <div className='text-left pr-5'>
                                            <h3 className={`${isActive ? 'text-white' : 'text-primary'} line-clamp-1 text-[18px] sm:text-[20px] leading-[23px] sm:leading-[25px] font-semibold`}>{title}</h3>
                                            {isActive && subtitle && (
                                                <p className={`${isActive ? 'text-white/90' : 'text-darkgray'} text-[14px] leading-6 tracking-[0.01em] font-normal mt-1 line-clamp-1 transition-opacity duration-200`}>{subtitle}</p>
                                            )}
                                        </div>
                                    </div>

                                    <svg width="20" height="20" className='min-w-5' viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M14.4132 10.6632L8.16325 16.9132C7.98713 17.0894 7.74826 17.1883 7.49918 17.1883C7.25011 17.1883 7.01124 17.0894 6.83512 16.9132C6.659 16.7371 6.56006 16.4983 6.56006 16.2492C6.56006 16.0001 6.659 15.7612 6.83512 15.5851L12.4218 9.99997L6.83668 4.41325C6.74948 4.32604 6.6803 4.22251 6.63311 4.10857C6.58591 3.99463 6.56162 3.87251 6.56162 3.74918C6.56162 3.62586 6.58591 3.50374 6.63311 3.3898C6.6803 3.27586 6.74948 3.17233 6.83668 3.08512C6.92389 2.99792 7.02742 2.92874 7.14136 2.88155C7.2553 2.83435 7.37742 2.81006 7.50075 2.81006C7.62408 2.81006 7.7462 2.83435 7.86014 2.88155C7.97408 2.92874 8.0776 2.99792 8.16481 3.08512L14.4148 9.33512C14.5021 9.42232 14.5713 9.5259 14.6185 9.63991C14.6657 9.75392 14.6899 9.87612 14.6898 9.99951C14.6896 10.1229 14.6651 10.245 14.6177 10.3589C14.5702 10.4728 14.5008 10.5763 14.4132 10.6632Z" fill={isActive ? 'white' : '#475467'} />
                                    </svg>
                                </button>
                            )
                        })}
                    </div>

                    <div className='lg:max-w-[623px] lg:mx-0 mx-auto w-full'>
                        <ImageWithTransition key={active.id} src={active.img} alt={t(`landing.features.${active.id}.title`)} width={623} height={509} />
                    </div>
                </div>
            </div>
        </div>
    )
}

export default OptimizeOperations